import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { operate, type Result } from "@/config";

/**
 * Running a write, and refreshing what it changed.
 *
 * ## What this replaces
 *
 * A Server Action plus `revalidatePath`. The action forwarded the form and named
 * the pages whose data was now stale; here the mutation names the query keys
 * instead. Same idea, same responsibility in the same place — the caller says
 * what its write invalidated, because only the caller knows.
 *
 * ## Why it never throws
 *
 * `operate` returns the API's own result, `fieldErrors` and all, and this passes
 * it straight back. That chain is the fix for the prototype's single
 * `catch { setError(t('common.error')) }`, which collapsed `email_in_use`,
 * `invalid_supervisor`, `missing_fields` and `upload_error` into one "something
 * went wrong" — duplicate email being by far the likeliest real failure, and
 * indistinguishable from the server being down.
 *
 * A success toast is shown from the API's own `message`, never from a string
 * invented here. The prototype had three translated success messages
 * (`guards.createdSuccess`, `leave.decisionSaved`, `zones.guardsAssignedSaved`)
 * that nothing ever rendered; every mutation now confirms itself.
 */
export function useOperate<T = undefined>(
  name: string,
  options: { invalidate?: (readonly unknown[])[]; onSuccess?: (data: T) => void } = {},
) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const { invalidate, onSuccess } = options;

  const run = useCallback(
    async (payload: Record<string, unknown>): Promise<Result<T>> => {
      setPending(true);
      const result = await operate<T>(name, payload);
      setPending(false);

      if (result.ok) {
        if (result.message) toast.success(result.message);
        /* Awaited, so a caller that navigates afterwards does not land on a
           screen still showing the pre-write data. */
        await Promise.all(
          (invalidate ?? []).map((key) => queryClient.invalidateQueries({ queryKey: key })),
        );
        onSuccess?.(result.data);
      } else {
        /* Always toast failures. Field errors are also rendered beside inputs,
           but an error for a missing control used to disappear entirely — the
           form kept typed values and looked as if it had saved. */
        toast.error(result.error);
      }

      return result;
    },
    [name, invalidate, onSuccess, queryClient],
  );

  return { run, pending };
}

/**
 * Turns a form into the JSON body the API expects.
 *
 * Repeated keys become an array, which is how a multi-select and a checkbox list
 * post. `File` entries are skipped: uploads are presigned separately and only the
 * resulting object key is submitted, so a photo never travels through here.
 */
export function payload(formData: FormData): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const [key, value] of formData.entries()) {
    if (typeof value !== "string") continue;

    const existing = out[key];
    if (existing === undefined) out[key] = value;
    else if (Array.isArray(existing)) existing.push(value);
    else out[key] = [existing, value];
  }

  return out;
}
